import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';

import HR from '@zg-rentals/multibrand-ui/lib/HR';
import Table from '@zg-rentals/multibrand-ui/lib/Table';
import Switch from '@zg-rentals/multibrand-ui/lib/Switch';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Input from '@zg-rentals/multibrand-ui/lib/Input';

import { Container } from '@zillow/zrs-comet';

import {
    DEV_BRAND,
    HOST_APP,
    INSTANT_APP_ENABLED,
    ZG_API,
    ZG_APPLICATIONS_ENABLED,
    ZG_APPLICATIONS_VIEWABLE_GROUP,
    ZG_MOCK_USER_EMAIL,
    ZG_MOCK_USER_ENABLED,
    ZG_MOCK_USER_HAS_STRONG_PASSWORD,
    ZG_MOCK_USER_IMPERSONATOR_EMAIL,
    ZG_MOCK_USER_IMPERSONATOR_TOKEN,
    ZG_MOCK_USER_IS_AUTHENTICATED,
    ZG_MOCK_USER_TOKEN,
    ZG_PAYMENTS_ENABLED,
    ZG_RENTER_INFORM_ENABLED,
    ZG_LEAD_GUARANTEE_ENABLED,
    ZG_CUSTOMER_PAYMENT_ENABLED,
    ZG_LISTING_CLAIMING,
} from 'app/shared/constants/FeatureFlagConstants';
import sharedConfig from 'app/shared/sharedConfig';
import StringConstants from 'app/shared/constants/StringConstants';
import AppActions from 'app/shared/actions/AppActions';
import AppSelectors from 'app/shared/selectors/AppSelectors';
import EnvSelectors from 'app/shared/selectors/EnvSelectors';

import './style.scss';

class FeatureFlagPage extends React.Component {
    static propTypes = {
        featureFlags: PropTypes.object.isRequired,
        zgApi: PropTypes.string.isRequired,
    }

    // local state used to control form input value. State keys must be the same as cookie name
    // due to current implementation does sth like `Cookies.set('userid')`.
    state = {
        userid: Cookies.get('userid'),
        ZILLOW_SSID: Cookies.get('ZILLOW_SSID'),
        DEV_BRAND: Cookies.get('DEV_BRAND'),
        SESSION_TOKEN: Cookies.get('SESSION_TOKEN'),
        ZG_API: Cookies.get('ZG_API'),
        ZG_MOCK_USER_TOKEN: Cookies.get('ZG_MOCK_USER_TOKEN'),
        ZG_MOCK_USER_EMAIL: Cookies.get('ZG_MOCK_USER_EMAIL'),
        ZG_MOCK_USER_IMPERSONATOR_TOKEN: Cookies.get('ZG_MOCK_USER_IMPERSONATOR_TOKEN'),
        ZG_MOCK_USER_IMPERSONATOR_EMAIL: Cookies.get('ZG_MOCK_USER_IMPERSONATOR_EMAIL'),
    }

    handleToggle(name, value) {
        return () => {
            const { dispatch } = this.props;
            dispatch(AppActions.updateFeatureFlag(name, value));
        };
    }

    handleSelectChange(name) {
        return (event) => {
            const { dispatch } = this.props;
            dispatch(AppActions.updateFeatureFlag(name, event.target.value));
            if (name === DEV_BRAND) {
                window.location.reload();
            }
        };
    }

    handleInputChange = name => (event) => {
        const { value } = event.target;

        this.updateCookie(name, value);
    }

    renderToggles(toggles) {
        const { featureFlags } = this.props;
        return toggles.map(feature => (
            <Table.Row key={feature} isDefaultExpanded={true}>
                <Table.Col>{feature}</Table.Col>
                <Table.Col>
                    <Switch
                        on={featureFlags[feature]}
                        onClick={this.handleToggle(feature, !featureFlags[feature])}
                    />
                </Table.Col>
            </Table.Row>
        ));
    }

    updateCookie = (name, value) => {
        // Custom converter to override default encoding behavior for special characters `%`
        // https://github.com/js-cookie/js-cookie#encoding
        Cookies.withConverter({
            write: _ => _,
        }).set(name, value);

        this.setState({ [name]: value });
    }

    renderSelects() {
        const { featureFlags, zgApi } = this.props;

        const selects = [{
            name: DEV_BRAND,
            values: Object.values(StringConstants.UNFORMATTED_BRAND_NAMES),
        }, {
            name: ZG_API,
            values: [...new Set([zgApi, ...Object.values(sharedConfig.zgApi)])],
        }, {
            name: ZG_APPLICATIONS_VIEWABLE_GROUP,
            values: ['none', 'all'],
        }, {
            name: HOST_APP,
            values: ['none', 'ios', 'android'],
        }];

        return selects.map(({ name, values }) => {
            // if cookie does not exist
            if (typeof Cookies.get(name) === 'undefined') {
                // use default value
                this.updateCookie(name, featureFlags[name]);
            }

            return (
                <Select
                    key={name}
                    className="FeatureFlagPage-Select"
                    label={`Select ${name}`}
                    value={featureFlags[name]}
                    onChange={this.handleSelectChange(name)}
                >
                    {values.map(value => (<option key={value} value={value}>{value}</option>))}
                </Select>
            );
        });
    }

    renderInputs(inputs) {
        return inputs.map(name => (
            <Table.Row key={name} isDefaultExpanded={true}>
                <Table.Col>{name}</Table.Col>
                <Table.Col>
                    <Input
                        value={this.state[name]}
                        onChange={this.handleInputChange(name)}
                        placeholder={name}
                    />
                </Table.Col>
            </Table.Row>
        ));
    }

    getFeatureFlagInputCookieNames() {
        const { featureFlags } = this.props;
        return featureFlags[DEV_BRAND] === sharedConfig.brands.zillow
            ? ['userid', 'ZILLOW_SSID']
            : ['SESSION_TOKEN'];
    }

    getMockUserInputCookieNames() {
        return [
            ZG_MOCK_USER_TOKEN,
            ZG_MOCK_USER_EMAIL,
            ZG_MOCK_USER_IMPERSONATOR_TOKEN,
            ZG_MOCK_USER_IMPERSONATOR_EMAIL];
    }

    render() {
        return (
            <Container className="FeatureFlagPage">
                {this.renderSelects()}
                <HR>Cookies</HR>
                <Table isExpandable={false}>
                    <Table.Row>
                        <Table.HeaderCol>Cookie Name</Table.HeaderCol>
                        <Table.HeaderCol>Cookie Value</Table.HeaderCol>
                    </Table.Row>
                    {this.renderInputs(this.getFeatureFlagInputCookieNames())}
                </Table>
                <HR>Features</HR>
                <Table isExpandable={false}>
                    <Table.Row>
                        <Table.HeaderCol>Feature Name</Table.HeaderCol>
                        <Table.HeaderCol>Feature Status</Table.HeaderCol>
                    </Table.Row>
                    {this.renderToggles([
                        ZG_APPLICATIONS_ENABLED,
                        ZG_PAYMENTS_ENABLED,
                        ZG_RENTER_INFORM_ENABLED,
                        ZG_LEAD_GUARANTEE_ENABLED,
                        ZG_CUSTOMER_PAYMENT_ENABLED,
                        ZG_LISTING_CLAIMING,
                        INSTANT_APP_ENABLED])}
                </Table>
                <HR>Mock User</HR>
                <Table isExpandable={false}>
                    {this.renderToggles([ZG_MOCK_USER_ENABLED])}
                    {this.renderInputs(this.getMockUserInputCookieNames())}
                    {this.renderToggles([
                        ZG_MOCK_USER_IS_AUTHENTICATED,
                        ZG_MOCK_USER_HAS_STRONG_PASSWORD])}
                </Table>
            </Container>
        );
    }
}

const mapStateToProps = state => ({
    featureFlags: AppSelectors.makeCookieEnabledServices(state),
    zgApi: EnvSelectors.makeZgApi(state),
});

export default connect(mapStateToProps)(FeatureFlagPage);
